import { useMemo, useCallback, useEffect, useState } from 'react'
import moment, { type Moment } from 'moment'
import { StringParam, useQueryParam } from 'use-query-params'
import { usePartnerContext } from '@retailer-platform/dashboard/utils'
import { DateRangePreset } from '../components/date-range-preset-select/ReportsNormalizedReportsListDateRangePresetSelect'
import { type ReportTypeEnum, usePaginatedSearchNormalizedReportsQuery } from '../../../api'
import { type ReportTypeConfig } from './ReportsNormalizedReportsList.utils'

interface Props {
  partnerId: string
  retailerId: string
  reportTypesConfig: ReportTypeConfig
}

const offsetsDays = {
  [DateRangePreset.Today]: 0,
  [DateRangePreset.Yesterday]: 1,
  [DateRangePreset.Last7Days]: 7,
  [DateRangePreset.Last30Days]: 30,
  [DateRangePreset.Last6Months]: 180,
}

export interface MomentRange {
  startDate: Moment
  endDate: Moment
}

const format = 'YYYY-MM-DD'

export const useReportsNormalizedReportsList = (props: Props) => {
  const { warehouses } = usePartnerContext()
  const [retailerIds, setRetailerIds] = useState<string[]>(
    props.retailerId && props.retailerId !== '0' ? [props.retailerId] : []
  )

  const [reportType, setReportType] = useQueryParam<ReportTypeEnum, ReportTypeEnum>('reportType')
  const [reportName, setReportName] = useQueryParam('reportName', StringParam)
  const [manualStartDate, setManualStartDate] = useQueryParam('startDate', StringParam)
  const [manualEndDate, setManualEndDate] = useQueryParam('endDate', StringParam)
  const [storeGroupId, setStoreGroupId] = useQueryParam('storeGroup', StringParam)
  const [dateRangePreset = DateRangePreset.Last7Days, setDateRangePreset] = useQueryParam<
    DateRangePreset,
    DateRangePreset
  >('dateRangePreset')

  // isHeavyQuery is used to skip querying the api when query is likely to time out.
  // @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check
  const isHeavyQuery = warehouses.length > 5 && retailerIds.length === 0 && !reportType

  // @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check
  const warehouseOptions = warehouses.map(warehouse => ({
    label: warehouse.name,
    value: String(warehouse.id),
  }))

  // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
  const manualDateRange: MomentRange = useMemo(
    () => ({
      startDate: manualStartDate && moment(manualStartDate, format),
      endDate: manualEndDate && moment(manualEndDate, format),
    }),
    [manualStartDate, manualEndDate]
  )

  const setDateRange = useCallback(
    ({ startDate, endDate }: MomentRange) => {
      if (startDate) setManualStartDate(startDate.format(format))
      if (endDate) setManualEndDate(endDate.format(format))
    },
    [setManualStartDate, setManualEndDate]
  )

  useEffect(() => {
    if (dateRangePreset === DateRangePreset.Custom) return

    setManualStartDate(undefined)
    setManualEndDate(undefined)
  }, [dateRangePreset, setManualStartDate, setManualEndDate])

  const dateRange = useMemo(() => {
    if (dateRangePreset === DateRangePreset.Custom) return manualDateRange

    return {
      startDate: moment().subtract(offsetsDays[dateRangePreset], 'days'),
      endDate: null,
    }
  }, [dateRangePreset, manualDateRange])

  const formattedDates = useMemo(
    () => ({
      startDate: dateRange.startDate?.format(format),
      endDate: dateRange.endDate?.format(format),
    }),
    [dateRange]
  )

  const retailerIdsFilterValues =
    // @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check
    retailerIds.length === 0 ? warehouses.map(w => String(w.id)) : retailerIds

  const { loading, refetch, loadMore, canLoadMore, apiResult, error } =
    usePaginatedSearchNormalizedReportsQuery({
      variables: {
        ...formattedDates,
        reportType,
        partnerId: props.partnerId,
        // TODO: temp solution to get around the current auth. Will remove after updating the BE
        // @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check
        retailerId: props.retailerId === '0' ? String(warehouses[0].id) : props.retailerId,
        retailerIds: retailerIdsFilterValues,
        retailerLocationGroupId: storeGroupId,
      },
      skip: !formattedDates.startDate || isHeavyQuery,
    })

  let filteredResults = apiResult

  if (apiResult) {
    // filter out reports that are not in the reportTypesConfig
    const supportedReportTypes = Object.keys(props.reportTypesConfig)
    filteredResults = apiResult?.filter(({ reportType }) =>
      supportedReportTypes.includes(reportType)
    )

    // If the reportName query parameter is present,
    // filter the results by the reportName value.
    if (reportName) {
      // @ts-expect-error TS2339 — strictNullChecks. TODO: fix strict null check
      filteredResults = filteredResults?.filter(({ name }) => name.includes(reportName))
    }

    // add banner name to each report
    // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
    filteredResults = filteredResults.map(report => ({
      // @ts-expect-error TS2698 — strictNullChecks. TODO: fix strict null check
      ...report,
      // @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check
      banner: warehouses.find(warehouse => Number(warehouse.id) === Number(report.retailerId))
        ?.name,
    }))
  }

  // A higher-order function to clear the 'reportName' query parameter value.
  // This is to provide a nicer user experience. The reason it helps is so that the
  // user does not unknowingly filter by the report name after changing one of their
  // filters (since there is no way to clear the reportName filter on the UI).
  const useClearReportName = <T extends (...args: never[]) => unknown>(otherFunction: T) =>
    useCallback(
      (...args: Parameters<T>) => {
        setReportName(null)
        return otherFunction(...args)
      },
      [otherFunction]
    )

  return {
    apiResult: filteredResults,
    error,
    loading,
    refetch,
    loadMore,
    canLoadMore,
    dateRangePreset,
    setDateRangePreset: useClearReportName(setDateRangePreset),
    dateRange,
    setDateRange: useClearReportName(setDateRange),
    reportType,
    setReportType: useClearReportName(setReportType),
    warehouseOptions,
    retailerIds,
    setRetailerIds,
    storeGroupId: storeGroupId ?? '',
    setStoreGroupId,
    isHeavyQuery,
  }
}
